import React from 'react';
import styled from 'styled-components/macro';
import { Title, Body, Flex, Tooltip } from '@cognite/cogs.js';
import { Milestone } from 'types/dt-graphql-apollo-v2';
import { formatDateRecentTime } from 'utils/misc';
import { ProgressBar } from 'components';
import { getMilestoneProgress } from 'utils/project-ruleset';
import { sortRunsByDate } from 'utils/data-validation';
import { RuleRunStatus } from 'types/graphql-apollo-v2';
import { CollectionStatistics } from './CollectionStatistics';

export const CollectionCard = ({
  collection,
  allCollections,
  onOpen,
}: {
  collection: Milestone;
  allCollections: Milestone[];
  onOpen: VoidFunction;
}) => {
  const { rules, schemaCollection } = collection;

  const validRules =
    rules?.items.filter((rule) => rule!.ruleRunStatus!.items.length > 0) || [];
  const sortedRuns = sortRunsByDate(
    (validRules![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
  );

  const lastRun = sortedRuns[0]?.runAt;
  const timelineProgress = getMilestoneProgress(allCollections, collection);

  return (
    <StyledCard key={collection.externalId} direction="column" onClick={onOpen}>
      <div>
        <StyledTitle level={4}>
          {lastRun ? formatDateRecentTime(lastRun) : '-'}
        </StyledTitle>
        <StyledTitle level={3}>{collection.name}</StyledTitle>
      </div>

      <StyledTitle level={5}>{schemaCollection?.name}</StyledTitle>

      <CollectionStatistics collection={collection} showDifference />

      {timelineProgress === null ? (
        <Body level={5}>No time frame</Body>
      ) : (
        <div>
          <Body level={5}>
            {timelineProgress === 100 ? 'Timeline: Completed' : 'Timeline'}
          </Body>
          <Tooltip content={`${timelineProgress}% complete`}>
            <ProgressBar value={timelineProgress || 0} />
          </Tooltip>
        </div>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Flex)`
  background: var(--cogs-white);
  width: 22%;
  max-width: 400px;
  box-shadow: 0px 0px 7px var(--cogs-greyscale-grey3);
  transition: all 250ms linear;
  cursor: pointer;
  border-radius: 5px;
  padding: 1rem 1.5rem;
  gap: 1rem;

  &:hover {
    background: var(--cogs-greyscale-grey2);
  }

  .cogs-title-4 {
    margin-bottom: 0.2rem;
    color: var(--cogs-greyscale-grey7);
  }
  .cogs-label {
    width: 40px;
  }
`;

const StyledTitle = styled(Title)`
  overflow: hidden;
  text-overflow: ellipsis;
`;
